import clsx from "clsx";
import Modal from "../Modal/Modal";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import CheckBox from "../CheckBox/CheckBox";
import { useTranslation } from "react-i18next";
import {
  MdClose,
  MdDeselect,
  MdOutlineBorderClear,
  MdOutlineInterests,
  MdOutlinePalette,
  MdOutlineTouchApp,
  MdOutlineUnfoldMore,
  MdOutlineViewColumn,
  MdSelectAll,
} from "react-icons/md";
import useModalsStore from "src/store/modalsStore";
import useCollapsibles from "src/hooks/useCollapsibles";
import { ROUTES } from "src/helpers/routes";
import { useNavigate } from "react-router-dom";
import SettingsPerDeviceInfoBlock from "../InfoBlock/SettingsPerDeviceInfoBlock/SettingsPerDeviceInfoBlock";
import { useMemo } from "react";
import useUiPreferences from "src/store/useUiPreferences";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

const TableSettingsModal: React.FC = () => {
  const { t } = useTranslation(["modals", "common"]);
  const tablesUiPreferences = useUiPreferences((s) => s.tables);
  const setUiPreferences = useUiPreferences((s) => s.setUiPreferences);

  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();
  const navigate = useNavigate();

  const { setShowTableSettingsModal, setShowPaginationSettingsModal } =
    useModalsStore();

  const isIconsAllSelected = useMemo(() => {
    return (
      tablesUiPreferences.showSourceIcon &&
      tablesUiPreferences.showCategoryIcon &&
      tablesUiPreferences.showStatusIcon &&
      tablesUiPreferences.showIncomeIcon &&
      tablesUiPreferences.showExpenseIcon &&
      tablesUiPreferences.showDifferenceIcon &&
      tablesUiPreferences.showDateIcon &&
      tablesUiPreferences.showTimeIcon &&
      tablesUiPreferences.showMonthIcon &&
      tablesUiPreferences.showCountIcon &&
      tablesUiPreferences.showAccountIcon &&
      tablesUiPreferences.showBankIcon
    );
  }, [
    tablesUiPreferences.showSourceIcon,
    tablesUiPreferences.showCategoryIcon,
    tablesUiPreferences.showStatusIcon,
    tablesUiPreferences.showIncomeIcon,
    tablesUiPreferences.showExpenseIcon,
    tablesUiPreferences.showDifferenceIcon,
    tablesUiPreferences.showDateIcon,
    tablesUiPreferences.showTimeIcon,
    tablesUiPreferences.showMonthIcon,
    tablesUiPreferences.showCountIcon,
    tablesUiPreferences.showAccountIcon,
    tablesUiPreferences.showBankIcon,
  ]);

  const selectAllIcons = () => {
    setUiPreferences({
      tables: {
        showSourceIcon: true,
        showCategoryIcon: true,
        showStatusIcon: true,
        showIncomeIcon: true,
        showExpenseIcon: true,
        showDifferenceIcon: true,
        showDateIcon: true,
        showTimeIcon: true,
        showMonthIcon: true,
        showCountIcon: true,
        showAccountIcon: true,
        showBankIcon: true,
      },
    });
  };

  const deselectAllIcons = () => {
    setUiPreferences({
      tables: {
        showSourceIcon: false,
        showCategoryIcon: false,
        showStatusIcon: false,
        showIncomeIcon: false,
        showExpenseIcon: false,
        showDifferenceIcon: false,
        showDateIcon: false,
        showTimeIcon: false,
        showMonthIcon: false,
        showCountIcon: false,
        showAccountIcon: false,
        showBankIcon: false,
      },
    });
  };

  return (
    <Modal
      onEscape={() => setShowTableSettingsModal(false)}
      className={clsx(["!w-full", "md:!min-w-[600px]", "md:!max-w-[600px]"])}
    >
      <Modal.Header
        onClose={() => setShowTableSettingsModal(false)}
        title={t("modals:table_settings.title")}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-4", "w-full", "pb-2"])}>
          <SettingsPerDeviceInfoBlock />
          <CollapsibleBlock
            title={t("modals:table_settings.collapsibles.columns.title")}
            // open={collapsiblePreferences.tablePreferencesColumnsOpen}
            blocked
            open
            onChange={(isOpen: boolean) => {
              updateCollapsible({ tablePreferencesColumnsOpen: isOpen });
            }}
            icon={<MdOutlineViewColumn size={ICON_SIZES.SM} />}
          >
            <div
              className={clsx([
                "grid",
                "w-full",
                "gap-4",
                "grid-cols-1",
                "md:grid-cols-2",
              ])}
            >
              <CheckBox
                checked={tablesUiPreferences.allowPinnedColumns}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      allowPinnedColumns: value,
                    },
                  });
                }}
                label={t(
                  "modals:table_settings.collapsibles.columns.fixed_columns",
                )}
                description={t(
                  "modals:table_settings.collapsibles.columns.fixed_columns_description",
                )}
              />
              <CheckBox
                checked={tablesUiPreferences.showColumnFilters}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showColumnFilters: value,
                    },
                  });
                }}
                label={t(
                  "modals:table_settings.collapsibles.columns.columns_filter",
                )}
                description={t(
                  "modals:table_settings.collapsibles.columns.columns_filter_description",
                )}
              />
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("modals:table_settings.collapsibles.borders.title")}
            // open={collapsiblePreferences.tablePreferencesBordersOpen}
            blocked
            open
            onChange={(isOpen: boolean) => {
              updateCollapsible({ tablePreferencesBordersOpen: isOpen });
            }}
            icon={<MdOutlineBorderClear size={ICON_SIZES.SM} />}
          >
            <div
              className={clsx([
                "grid",
                "w-full",
                "gap-4",
                "grid-cols-1",
                "md:grid-cols-2",
              ])}
            >
              <CheckBox
                checked={tablesUiPreferences.showTableBorder}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showTableBorder: value,
                    },
                  });
                }}
                label={t(
                  "modals:table_settings.collapsibles.borders.table.title",
                )}
                description={t(
                  "modals:table_settings.collapsibles.borders.table.description",
                )}
              />
              <CheckBox
                checked={tablesUiPreferences.showColumnBorders}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showColumnBorders: value,
                    },
                  });
                }}
                label={t(
                  "modals:table_settings.collapsibles.borders.columns.title",
                )}
                description={t(
                  "modals:table_settings.collapsibles.borders.columns.description",
                )}
              />
              <CheckBox
                checked={tablesUiPreferences.showRowBorders}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showRowBorders: value,
                    },
                  });
                }}
                label={t(
                  "modals:table_settings.collapsibles.borders.rows.title",
                )}
                description={t(
                  "modals:table_settings.collapsibles.borders.rows.description",
                )}
              />
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("modals:table_settings.collapsibles.click.title")}
            // open={collapsiblePreferences.tablePreferencesClickBehaviorOpen}
            blocked
            open
            onChange={(isOpen: boolean) => {
              updateCollapsible({ tablePreferencesClickBehaviorOpen: isOpen });
            }}
            icon={<MdOutlineTouchApp size={ICON_SIZES.SM} />}
          >
            <div
              className={clsx([
                "grid",
                "w-full",
                "gap-4",
                "grid-cols-1",
                "md:grid-cols-2",
              ])}
            >
              <CheckBox
                checked={tablesUiPreferences.selectionOnClickAnyCell}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      selectionOnClickAnyCell: value,
                    },
                  });
                }}
                label={t(
                  "modals:table_settings.collapsibles.click.select_cell",
                )}
                description={t(
                  "modals:table_settings.collapsibles.click.select_cell_description",
                )}
              />
              <CheckBox
                checked={tablesUiPreferences.allowMultipleSelection}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      allowMultipleSelection: value,
                    },
                  });
                }}
                label={t(
                  "modals:table_settings.collapsibles.click.allow_multiple_selection",
                )}
                description={t(
                  "modals:table_settings.collapsibles.click.allow_multiple_selection_description",
                )}
              />
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("modals:table_settings.collapsibles.icons.title")}
            icon={<MdOutlineInterests size={ICON_SIZES.SM} />}
            // open={collapsiblePreferences.tablePreferencesIconsOpen}
            blocked
            open
            onChange={(isOpen: boolean) => {
              updateCollapsible({ tablePreferencesIconsOpen: isOpen });
            }}
            buttons={[
              {
                onClick: () => selectAllIcons(),
                tooltip: "Seleccionar todo",
                icon: <MdSelectAll />,
                hide: isIconsAllSelected,
              },
              {
                onClick: () => deselectAllIcons(),
                tooltip: "Deseleccionar todo",
                icon: <MdDeselect />,
                hide: !isIconsAllSelected,
              },
            ]}
          >
            <div
              className={clsx([
                "grid",
                "w-full",
                "gap-2",
                "grid-cols-2",
                "md:grid-cols-3",
              ])}
            >
              <CheckBox
                checked={tablesUiPreferences.showSourceIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showSourceIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.source")}
              />
              <CheckBox
                checked={tablesUiPreferences.showCategoryIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showCategoryIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.category")}
              />
              <CheckBox
                checked={tablesUiPreferences.showStatusIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showStatusIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.status")}
              />
              <CheckBox
                checked={tablesUiPreferences.showIncomeIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showIncomeIcon: value,
                    },
                  });
                }}
                label={t("misc:summary.income")}
              />
              <CheckBox
                checked={tablesUiPreferences.showExpenseIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showExpenseIcon: value,
                    },
                  });
                }}
                label={t("misc:summary.expenses")}
              />
              <CheckBox
                checked={tablesUiPreferences.showDifferenceIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showDifferenceIcon: value,
                    },
                  });
                }}
                label={t("misc:summary.diff")}
              />
              <CheckBox
                checked={tablesUiPreferences.showDateIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showDateIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.date")}
              />
              <CheckBox
                checked={tablesUiPreferences.showTimeIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showTimeIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.time")}
              />
              <CheckBox
                checked={tablesUiPreferences.showMonthIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showMonthIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.month")}
              />
              <CheckBox
                checked={tablesUiPreferences.showCountIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showCountIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.count")}
              />
              <CheckBox
                checked={tablesUiPreferences.showAccountIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showAccountIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.account")}
              />
              <CheckBox
                checked={tablesUiPreferences.showBankIcon}
                onChange={(value) => {
                  setUiPreferences({
                    tables: {
                      showBankIcon: value,
                    },
                  });
                }}
                label={t("common:table_headings.bank")}
              />
            </div>
          </CollapsibleBlock>
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("common:buttons.close"),
            onClick: () => setShowTableSettingsModal(false),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
          },
        ]}
        leftContent={
          <ButtonGroup
            buttons={[
              {
                kind: EButtonKind.NORMAL,
                icon: <MdOutlinePalette />,
                onClick: () => {
                  setShowTableSettingsModal(false);
                  navigate(ROUTES.SETTINGS_APPEARANCE);
                },
                tooltip: t("pages:settings.blocks.appearance.title"),
              },
              {
                icon: <MdOutlineUnfoldMore className={clsx(["rotate-90"])} />,
                onClick: () => {
                  setShowTableSettingsModal(false);
                  setShowPaginationSettingsModal(true);
                },
                tooltip: "Paginación",
              },
            ]}
          />
        }
      />
    </Modal>
  );
};

export default TableSettingsModal;
